import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
} from 'react'

export interface ObservationFrameEngagementValue {
  // A popover opened from the node portals out of it, taking focus along, but
  // the node is still being worked on while it is open. Each popover reports
  // under its own id, so several can be open at once.
  setPopoverOpen: (open: boolean, id?: string) => void
  // Opens the node's details, the note editor focused when asked; absent
  // where the node has no details to open.
  openDetails?: (focus?: 'note') => void
  // The note slot tells the frame whether it holds a note, so the frame can
  // offer "Add note" for an empty one; once asked, the slot shows its editor.
  reportNoteSlot?: (state: 'empty' | 'filled' | null) => void
  noteRequested?: boolean
  // Opens the node's History; absent where there is none to show.
  openHistory?: () => void
}

export const ObservationFrameEngagement =
  createContext<ObservationFrameEngagementValue>({
    setPopoverOpen: () => {},
  })

// For a picker inside an observation node: report its popover opening and
// closing to the node's frame. Reported as the open state changes, before
// focus moves into the popover, and withdrawn if the picker unmounts open.
export function useReportPopoverOpen(
  initialOpen: boolean
): (open: boolean) => void {
  const { setPopoverOpen } = useContext(ObservationFrameEngagement)
  const id = useId()
  const openRef = useRef(false)
  // Reported once per change: a picker may say "open" from both its own
  // handler and an effect.
  const report = useCallback(
    (open: boolean): void => {
      if (openRef.current === open) {
        return
      }
      openRef.current = open
      setPopoverOpen(open, id)
    },
    [setPopoverOpen, id]
  )
  useEffect(() => {
    if (initialOpen) {
      report(true)
    }
  }, [initialOpen, report])
  useEffect(
    () => () => {
      if (openRef.current) {
        setPopoverOpen(false, id)
      }
    },
    [setPopoverOpen, id]
  )
  return report
}
